import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { CallZimService } from './call-zim.service';
import { ZimService } from '../../../services/zim.service';
import { CallState, CallType, CallParticipant, CurrentCall, CallInvitation, PayloadDict, CallStoreState, SwitchCallExtendedData, NetworkQualityInfo, NetworkQualityData, RemoteNetworkQualityData, CallEndReason } from '../types/call.d';
import { ZIM, ZIMCallAcceptConfig, ZIMCallCancelConfig, ZIMCallEndConfig, ZIMCallingInviteConfig, ZIMCallInviteConfig, ZIMCallJoinConfig, ZIMCallQuitConfig, ZIMCallRejectConfig, ZIMCallUserInfo, ZIMPushConfig, ZIMUserFullInfo, ZIMUsersInfoQueriedResult } from 'zego-zim-web';
import { CallZegoService } from './call-zego.service';
import ZegoLocalStream from 'zego-express-engine-webrtc/sdk/code/zh/ZegoLocalStream.web';
import { getListContactFromLocal, getUserInfo, safeJsonParse } from '../../../utils/utils';
import { ZegoSoundLevelInfo } from 'zego-express-engine-webrtc/sdk/code/zh/ZegoExpressEntity.web';
import { replacePhoneNumber } from '../../../constants/until';
import { LocalMediaPermissionService } from '../../../services/local-media-permission.service';
import { BroadcastService } from '../../../services/broadcast.service';
import { MeetingManagementService } from '../../meeting-management/services/meeting-management.service';
import { RouteStateService } from './route-state.service';
import { CallAudioService } from './call-audio.service';
import { RetryService } from './retry.service';
import { ZegoQualityGrade } from '../constants';
import { BlockUserService } from '../../../services/block-user.service';

@Injectable({
  providedIn: 'root'
})
export class CallStoreService {
  private callZimService = inject(CallZimService);
  private zimService = inject(ZimService);
  private callZegoService = inject(CallZegoService);
  private localMediaPermissionService = inject(LocalMediaPermissionService);
  private broadcastService = inject(BroadcastService);
  private meetingManagementService = inject(MeetingManagementService);
  private readonly routeState = inject(RouteStateService);
  private readonly callAudio = inject(CallAudioService);
  private readonly retryService = inject(RetryService);
  private readonly blockUserService = inject(BlockUserService);


  // Signals for state management
  private readonly _currentCall = signal<CurrentCall | null>(null);
  private readonly _callState = signal<CallState>('idle');
  private readonly _participants = signal<CallParticipant[]>([]);
  private readonly _pendingInvitation = signal<CallInvitation | null>(null);

  private readonly _localStream = signal<ZegoLocalStream | null>(null);
  private readonly _localStreamId = signal<string>("");
  private readonly _localSoundLevel = signal<number>(0);
  private readonly _localMicState = signal<boolean>(false);
  private readonly _localCamState = signal<boolean>(false);

  private readonly _callStartTime = signal<number>(Date.now());

  private readonly _isMeClickAccept = signal<boolean>(false);
  private readonly _isMeClickCall = signal<boolean>(false);
  private readonly _isLoginRoom = signal<boolean>(false);

  private readonly _networkQualityInfo = signal<NetworkQualityInfo>({
    localState: { downstreamQuality: ZegoQualityGrade.Unknown, upstreamQuality: ZegoQualityGrade.Unknown },
    remoteState: []
  });

  // Readonly signals for components
  readonly currentCall = this._currentCall.asReadonly();
  readonly callState = this._callState.asReadonly();
  readonly pendingInvitation = this._pendingInvitation.asReadonly();
  readonly participants = this._participants.asReadonly();
  readonly localStream = this._localStream.asReadonly();
  readonly localStreamId = this._localStreamId.asReadonly();
  readonly localSoundLevel = this._localSoundLevel.asReadonly();
  readonly localMicState = this._localMicState.asReadonly();
  readonly localCamState = this._localCamState.asReadonly();
  readonly networkQualityInfo = this._networkQualityInfo.asReadonly();
  readonly callStartTime = this._callStartTime.asReadonly();

  readonly isInCallRoute = this.routeState.isInRoute("/call");
  readonly isMicPermissionGranted = this.localMediaPermissionService.isMicPermissionGranted;
  readonly isCamPermissionGranted = this.localMediaPermissionService.isCameraPermissionGranted;

  // Computed signals
  readonly isInCall = computed(() => this._callState() !== 'idle');
  readonly activeParticipants = computed(() =>
    this._participants().filter(p => p.state === ZIM.CallUserState.Accepted)
  );
  readonly hasPendingCall = computed(() =>
    this._pendingInvitation() &&
    this._callState() === 'idle'
  );
  readonly hasCalleeIncomingCall = computed(() =>
    this._pendingInvitation()
    && this._pendingInvitation()?.status !== 'waiting'
    && this.pendingInvitation()?.inviter !== this.zimService.userId
  );
  readonly showCallStatusBar = computed(() => {
    const state = this.callState();

    if (this.isInCallRoute()) return false;
    if (state === 'idle' || state === 'ended') return false;

    const call = this.currentCall();
    if (!call) return false;

    const isCaller = call.inviter === this.zimService.userId;
    if (isCaller) return true;

    // callee
    return state === 'connected';
  });

  readonly isInviter = computed(() => {
    const call = this.currentCall();
    return call && call.inviter === this.zimService.userId;
  });

  readonly isShowMyNetworkWarning = computed(() => {
    const info = this.networkQualityInfo();
    return info.localState.upstreamQuality === ZegoQualityGrade.Poor ||
          info.localState.downstreamQuality === ZegoQualityGrade.Poor;
  });

  readonly isShowMyNetworkReconnecting = computed(() => {
    const info = this.networkQualityInfo();
    return info.localState.upstreamQuality === ZegoQualityGrade.Die ||
          info.localState.downstreamQuality === ZegoQualityGrade.Die;
  });

  readonly isShowRemoteNetworkWarning = computed(() => {
    const info = this.networkQualityInfo();
    return info.remoteState.some(remote =>
      remote.upstreamQuality === ZegoQualityGrade.Poor ||
      remote.downstreamQuality === ZegoQualityGrade.Poor ||
      remote.upstreamQuality === ZegoQualityGrade.Die ||
      remote.downstreamQuality === ZegoQualityGrade.Die
    );
  });

  // Khi chưa vào cuộc gọi sử dụng nó để hiển thị mạng
  readonly isOnlineStatus = computed(() => {
    return this.zimService.showOnlineStatus() || (this.callState() === 'outgoing' || this.callState() === 'ringing');
  });

  private networkQualityTimer: any = null;
  private readonly NETWORK_TIMEOUT = 30000; // 30 seconds

  localDieByDisconnect: boolean = false;

  constructor() {
    effect(()=> {
      if(this.zimService.isLoginZim() === true) {
        this.setupEventListeners();
        if (this.isInCallRoute()) {
          window.addEventListener('offline', () => {
            this.forceLocalNetworkDie('browser offline');
          });

          window.addEventListener('online', () => {
            this.onLocalReconnected();
          });
        }
      }
    })

    // this.callZegoService.getEngine().on("roomUserUpdate", this.handleRoomUserUpdate.bind(this));
    this.callZegoService.getEngine().on("remoteMicStatusUpdate", this.handleRemoteMicStateChange.bind(this));
    this.callZegoService.getEngine().on("remoteCameraStatusUpdate", this.handleRemoteCamStateChange.bind(this));
    this.callZegoService.getEngine().on("soundLevelUpdate", this.handleSoundLevelUpdate.bind(this));
    this.callZegoService.getEngine().on("networkQuality", this.handleNetworkQuality.bind(this));
  }

  private setupEventListeners(): void {
    // Lắng nghe nhận cuộc gọi đến
    this.callZimService.listenCallInvitationReceived(async (zim, data) => {
      // reject cuộc gọi nếu người gọi bị block
      if (this.blockUserService.checkIsBlackListUser(data.inviter)) {
        await this.rejectBlocked("audio", data.callID);
        return;
      }

      // xóa cuộc gọi đến đã quá thời gian chờ
      const currentPending = this._pendingInvitation();
      if (currentPending?.timeout && this.isExpired(currentPending.timeout)) {
        this._pendingInvitation.set(null);
        if (this._callState() !== 'connected') {
          this._callState.set('idle');
        }
      }

      const extendedData: PayloadDict = JSON.parse(data.extendedData);
      // gọi nhóm thì không làm gì
      if (extendedData.groupId) return;
      const participants = await this.getParticipants(data.callUserList, extendedData);
      const invitation: CallInvitation = {
        callId: data.callID,
        inviter: data.inviter,
        invitees: participants,
        type: !extendedData.isVideoCall ? 'audio' : 'video',
        timeout: new Date(),
        status: 'incoming',
        extendedData
      };

      switch (this._callState()) {
        // nếu có một cuộc gọi đến mà cuộc gọi hiện tại đang ở trạng thái đang chờ
        case 'outgoing':
        case 'ringing':
          if (currentPending) {
            await this.rejectBusy(invitation);
          } else {
            this.createNewInvitation("Received", data.callID, participants, extendedData, invitation);
          }
          break;

        // Nếu đã trong cuộc gọi
        case 'connected':
          invitation.status = 'waiting';
          // kiểm tra xem có cuộc gọi đến trong cuộc gọi chờ không
          // -> có thì call reject cuộc gọi đó
          if (currentPending && currentPending.callId  !== invitation.callId) {
            await this.rejectBusy(invitation);
          }
          // -> không thì thêm
          else {
            this._pendingInvitation.set(invitation);
          }
          break;

        case 'ended':
          window.close();
          break;

        default:
          this.createNewInvitation("Received", data.callID, participants, extendedData, invitation);
          break;
      }
    });

    // Lắng nghe khởi tạo cuộc gọi
    // có thể lắng nghe được cuộc gọi từ thiết bị khác sang gây lỗi
    this.callZimService.listenCallInvitationCreated(async (zim, data) => {
      // kiểm tra có phải mình gọi hay không
      if (!this._isMeClickCall()) return;
      const extendedData: PayloadDict = JSON.parse(data.extendedData);
      const participants = await this.getParticipants(data.callUserList, extendedData);

      this.createNewInvitation('Created', data.callID, participants, extendedData);

      const syncCall: CallStoreState = {
        currentCall: this.currentCall(),
        callState: 'outgoing',
        participants: participants,
        pendingInvitation: null
      }
      this.broadcastService.call({ action: 'sync-call-state', payload: syncCall });
    });

    // Trạng thái người dùng khi nghe cuộc gọi đã thay đổi.
    this.callZimService.listenCallUserStateChanged(async (zim, data) => {
      const call = this._currentCall();
      // Kiểm tra user thay đổi trạng thái trong cuộc gọi hiện tại không
      if (!call || call.callId !== data.callID) {
        // cuộc gọi thứ 3 xóa view khi có cuộc gọi đến được chấp nhận ở app rồi
        const myUser = data.callUserList.find(u => u.userID === this.zimService.userId);
        if (myUser
            && myUser.state === ZIM.CallUserState.Accepted
            && !this._isMeClickAccept()
            && this._callState() === 'connected') {
          this.broadcastService.call({ action: 'reset-call-state', payload: { callId: data.callID } })
          window.close();
        }
        return;
      };

      // Cập nhật trạng thái của các thành viên tham gia.
      this._participants.update(participants =>
        participants.map(p => {
          const userState = data.callUserList.find(u => u.userID === p.userId);
          if (userState) {
            return { ...p, state: userState.state };
          }
          return p;
        })
      );

      // Xử lý theo trạng thái
      for (const user of data.callUserList) {
        switch (user.state) {
          case ZIM.CallUserState.Inviting: // [Caller] đang gửi lời mời gọi
            this.updateRingtone();
            break;
          case ZIM.CallUserState.Received: // [Callee] đã nhận được cuộc gọi (incoming ringing)
            if(this._callState() === 'outgoing') {
              this._callState.set('ringing');
              this.broadcastService.call({ action: 'sync-call-status', payload: { callState: this._callState() } })
              this.updateRingtone();
            }
            break;
          case ZIM.CallUserState.Accepted: // [Cả 2] đã accept
            this.callAudio.stop();
            this.broadcastService.call({ action: 'clear-ringtone', payload: {} });
            if(this.isInCallRoute()){
              this.publishCallStream(call.callId, call.type);
            }

            const callState = this._callState();
            // xóa view khi có cuộc gọi đến được chấp nhận ở app rồi
            if (call.callId === data.callID
              && user.userID === this.zimService.userId
              && (callState === 'outgoing' || callState === 'ringing')
              && !this._isMeClickAccept()
            ) {
              this.reset();
              return;
            }

            // đặt thời gian bắt đầu cuộc gọi
            const now = Date.now();
            this._callStartTime.set(now);
            this.broadcastService.call({ action: 'sync-call-start-time', payload: { callStartTime: now } })

            // cập nhật trạng thái cuộc gọi cho người gọi
            if (call.inviter === this.zimService.userId) {
              this._currentCall.update(call => call ? { ...call, state: 'connected' } : null);

              this._callState.set('connected');
              this.updateRingtone();
              this._pendingInvitation.set(null);

              const syncCall: CallStoreState = {
                currentCall: this._currentCall(),
                callState: 'connected',
                participants: this._participants(),
                pendingInvitation: null
              }
              this.broadcastService.call({ action: 'sync-call-state', payload: syncCall })
            }
            break;
          case ZIM.CallUserState.Rejected: // [Cả 2] User reject
            const extendedData = safeJsonParse(user.extendedData);
            if (extendedData && extendedData.reason === 'busy_pre_room_calling') {
              this.handleCallInvitationError(data.callID, 'busy');
            } else if (extendedData && extendedData.reason === 'blocked') {
              this.handleCallInvitationError(data.callID, 'blocked');
            } else {
              this.handleCallInvitationError(data.callID, 'rejected');
            }
            break;
          case ZIM.CallUserState.Cancelled: // [Caller] hủy cuộc gọi
            console.log("[Caller] hủy cuộc gọi", user);
            break;
          case ZIM.CallUserState.Timeout: // [Cả 2] Không phản hồi → timeout
            const invitees: string[] = call.participants.map(p => p.userId);
            this.handleCallInvitationError(data.callID, 'timeout');
            if (call.inviter !== this.zimService.userId) {
              this.reset();
            } else {
              try {
                const config: ZIMCallCancelConfig = {
                  extendedData: ''
                };
                await this.callZimService.callCancel(invitees, call.callId, config);
              } catch (error) {
                console.error('Failed to cancel call:', error);
                throw error;
              }
            }
            break;
          case ZIM.CallUserState.Quit: // [Cả 2] User thoát khi đang trong call
            if(this.isSwitchCall(user.extendedData)) return;
            this.handleCallInvitationError(data.callID, 'normal');
            break;
          case ZIM.CallUserState.Ended: // [Cả 2] Cuộc gọi kết thúc bình thường
            // Xử lý cuộc gọi thứ 3
            if(this.isSwitchCall(user.extendedData)) return;
            this.handleCallInvitationError(data.callID, 'ended');
            break;
          case ZIM.CallUserState.NotYetReceived: // [Caller] Invitation chưa tới user
            break;
          case ZIM.CallUserState.BeCancelled: // [Callee] Invitation bị hủy trước khi nhận
              this.handleCallInvitationError(data.callID, 'cancelled');
            break;
        }
      }
    });

    // Lắng nghe thông báo lời mời gọi bị hủy
    this.callZimService.listenCallInvitationCancelled((zim, data) => {
      this.handleCallInvitationError(data.callID, 'cancelled');
    });

    // Lắng nghe thông báo hết thời gian chờ lời mời gọi
    this.callZimService.listenCallInvitationTimeout((zim, data) => {
      this.callAudio.stop();
      this.handleCallInvitationError(data.callID, 'timeout');
    });

    // Lắng nghe cuộc gọi kết thúc
    this.callZimService.listenCallInvitationEnded((zim, data) => {
      if(this.isSwitchCall(data.extendedData)) return;
      this.handleCallInvitationError(data.callID, 'ended');
    });
  }

  // Actions
  async startCall(invitees: string[], type: CallType = 'audio', extendedData: string = ''): Promise<void> {
    // đợi zim login
    await this.waitForServiceReady();

    // xử lý khi mình gọi cho b mà b gọi cho mình trước
    const hasInviteesInCall = invitees.find(inv => inv === this._pendingInvitation()?.inviter);
    if (this._pendingInvitation() && hasInviteesInCall) {
      this._callState.set('ended');
      this.updateRingtone();
      window.close();
      return;
    }

    this._isMeClickCall.set(true);
    try {
      const config: ZIMCallInviteConfig = {
        mode: ZIM.CallInvitationMode.Advanced,
        extendedData: extendedData,
        timeout: 60
      };

      const pushConfig: ZIMPushConfig = {
        resourcesID: 'appviettel_call',
        title: `Cuộc gọi đến từ ${this.zimService.userId ?? ''}`,
        content: 'Push Notification',
        enableBadge: true,
        badgeIncrement: 1,
        payload: extendedData
      };

      config.pushConfig = pushConfig;

      const result = await this.callZimService.callInvite(invitees, config);
      const callId = result.callID;
      const createCallBody = { isVideo: type === 'video', roomId: callId, calleeId: invitees, callerId: this.zimService.userId! }
      this.meetingManagementService.createCall(createCallBody).subscribe({
        next: (res) => {
          this._isMeClickCall.set(false);
        },
        error: (error) => {
          console.log("createCall", error);
          this._isMeClickCall.set(false);
        }
      });

    } catch (error: any) {
      console.error('Failed to start call:', error);
      this._isMeClickCall.set(false);
      // Handle specific ZIM errors
      if (error.code) {
        switch (error.code) {
          case 6000321: // User not exist
            throw new Error('Người dùng không tồn tại');
          case 6000322: // User offline
            throw new Error('Người dùng đang offline');
          case 6000323: // User in call
            throw new Error('Người dùng đang trong cuộc gọi khác');
          case 6000324: // User has pending invitation
            throw new Error('Người dùng đã có lời mời cuộc gọi đang chờ');
          default:
            throw new Error(`Không thể bắt đầu cuộc gọi: ${error.message || 'Lỗi không xác định'}`);
        }
      }
      throw error;
    }
  }

  async acceptCall(callId: string): Promise<void> {
    await this.waitForServiceReady();
    this._isMeClickAccept.set(true);
    this.callAudio.stop();
    this.broadcastService.call({ action: 'clear-ringtone', payload: {} });
    // Nếu đang trong cuộc gọi xử lý chuyển sang cuộc gọi mới.
    if (this._callState() === 'connected') {
      try {
        this.switchCall();
        return;
      } catch (error) {
        console.error('Failed to end current call before switching:', error);
        throw new Error('Không thể kết thúc cuộc gọi hiện tại');
      }
    }

    try {
      const config: ZIMCallAcceptConfig = {
        extendedData: ''
      };
      await this.callZimService.callAccept(callId, config);
      this.acceptIncomingCall();
    } catch (error) {
      console.error('Failed to accept call:', error);
      throw error;
    }
  }

  async acceptIncomingCall() {
    const incoming = this._pendingInvitation();
    if (!incoming) return;

    this._currentCall.set({
      callId: incoming.callId,
      type: incoming.type,
      state: 'connected',
      participants: incoming.invitees,
      startTime: new Date(),
      inviter: incoming.inviter,
      extendedData: incoming.extendedData
    });

    this._callState.set('connected');
    this.updateRingtone();
    this._participants.set(incoming.invitees);
    this._pendingInvitation.set(null);

    const syncCall: CallStoreState = {
      currentCall: this._currentCall(),
      callState: 'connected',
      participants: this._participants(),
      pendingInvitation: null
    }
    this.broadcastService.call({ action: 'sync-call-state', payload: syncCall });
    this._isMeClickAccept.set(false);
  }

  async switchCall() {
    const waiting = this._pendingInvitation();
    if (!waiting) return;

    const current = this._currentCall();
    if (!current) return;

    try {
      // End current
      const switchCallextendedData: SwitchCallExtendedData = {
        callID: current.callId,
        userId: this.zimService.userId ?? '',
        reason: 'switch-call'
      }
      const extendedData = JSON.stringify(switchCallextendedData);

      if (current.inviter === this.zimService.userId) {
        await this.callZimService.callEnd(current.callId, { extendedData });
      } else {
        await this.callZimService.callQuit(current.callId, { extendedData });
      }

      this.callZegoService.logoutRoom(current.callId);
      this.callZegoService.stopPubishAndDestroyStream(this.localStream(), this.localStreamId());

      // Accept new
      await this.callZimService.callAccept(waiting.callId, { extendedData: '' });
      this.publishCallStream(waiting.callId, waiting.type);
      // Update state
      this.acceptIncomingCall();
    } catch (error) {
      console.log(error);
    }
  }

  async rejectCall(callId: string, isRejectSwitchCall: boolean = false): Promise<void> {
    this._pendingInvitation.set(null);

    try {
      const config: ZIMCallRejectConfig = {
        extendedData: '',
      };
      await this.callZimService.callReject(callId, config);
      if (!isRejectSwitchCall) {
        this.handleCallInvitationError(callId, 'rejected');
      }
      // xóa cuộc gọi đến
      this.broadcastService.call({ action: 'clear-invitation', payload: { callId } })

      if (this._callState() !== 'connected') {
        this._callState.set('idle');
        this.updateRingtone();
        this.broadcastService.call({ action: 'clear-call-status', payload: { callId } })
      }

      if (this._callState() === 'ended') {
        window.close();
      }
    } catch (error) {
      console.error('Failed to reject call:', error);
      throw error;
    }
  }

  async rejectBusy(invitation: CallInvitation) {
    const busyRejectExtendedData = {
        type: invitation.type,
        callID: invitation.callId,
        reason: 'busy_pre_room_calling',
        endReason: 'BUSY',
        system: 'Web'
    }
    try {
      this.callZimService.callReject(invitation.callId, { extendedData: JSON.stringify(busyRejectExtendedData)});
      // xóa cuộc gọi đến
      this.broadcastService.call({ action: 'clear-invitation', payload: { callId: invitation.callId } })
    } catch (error) {
      console.log("rejectBusy err:", error);
    }
  }

  async rejectBlocked(type: CallType, callID: string) {
    const blockedRejectExtendedData = {
        type,
        callID,
        reason: 'blocked',
        endReason: 'BLOCKED',
        system: 'Web'
    }
    try {
      this.callZimService.callReject(callID, { extendedData: JSON.stringify(blockedRejectExtendedData)});
      // xóa cuộc gọi đến
      this.broadcastService.call({ action: 'clear-invitation', payload: { callId: callID } });
      this.broadcastService.call({ action: 'clear-ringtone', payload: {} });
      
    } catch (error) {
      console.log("rejectBlocked err:", error);
    }
  }

  async endCall(): Promise<void> {
    const call = this._currentCall();
    if (!call) return;

    try {
      const config: ZIMCallEndConfig = {
        extendedData: '',
      };

      await this.callZimService.callEnd(call.callId, config);
      this.handleCallInvitationError(call.callId, 'normal');
    } catch (error) {
      console.error('Failed to end call:', error);
      throw error;
    }
  }

  // Note
  async joinCall(callId: string): Promise<void> {
    try {
      const config: ZIMCallJoinConfig = {
        extendedData: ''
      };
      await this.callZimService.callJoin(callId, config);
      this._callState.set('connected');
      this.updateRingtone();
    } catch (error) {
      console.error('Failed to join call:', error);
      throw error;
    }
  }

  async callQuit(callId: string): Promise<void> {
    try {
      const config: ZIMCallQuitConfig = {
        extendedData: ''
      };
      await this.callZimService.callQuit(callId, config);
      this.handleCallInvitationError(callId, 'normal');
    } catch (error) {
      console.error('Failed to quit call:', error);
      throw error;
    }
  }

  async cancelCall(invitees: string[]): Promise<void> {
    const call = this._currentCall();
    if (!call) return;

    try {
      const config: ZIMCallCancelConfig = {
        extendedData: ''
      };
      await this.callZimService.callCancel(invitees, call.callId, config);
      this.handleCallInvitationError(call.callId, 'cancelled');
    } catch (error) {
      console.error('Failed to cancel call:', error);
      throw error;
    }
  }

  async callingInvite(invitees: string[], callId: string, payload: string): Promise<void> {
    try {
      const pushConfig: ZIMPushConfig = {
        resourcesID: 'appviettel_call',
        title: `Cuộc gọi đến từ ${this.zimService.userId ?? ''}`,
        content: 'Push Notification',
        enableBadge: true,
        badgeIncrement: 1,
        payload: payload
      };

      const config: ZIMCallingInviteConfig = {
        pushConfig
      };

      await this.callZimService.callingInvite(invitees, callId, config);
    } catch (error) {
      console.error('Failed to cancel call:', error);
      throw error;
    }
  }

  async getParticipants(callUserList: ZIMCallUserInfo[], extendedData: PayloadDict): Promise<CallParticipant[]> {
    const listCallUser = callUserList.filter(cu => cu.userID !== this.zimService.userId);
    let listContactLocalStorage = getListContactFromLocal();
    const conversationNameCache = JSON.parse(localStorage.getItem(`conversation_name_cache`) || '{}');
    let listCallParticipant: CallParticipant[] = [];
    const listUserID: string[] = callUserList.map(callUser => callUser.userID);
    let userInfoList: ZIMUsersInfoQueriedResult | undefined = undefined;
    try {
      userInfoList = await this.zimService.getUserInfo(listUserID);
    } catch (error) {
      console.log("userInfoList", error);
    }

    listCallUser.forEach((callUser: ZIMCallUserInfo) => {
      const contact = listContactLocalStorage.find((contact: any) => contact.zegoId === callUser.userID);
      const contactName = contact?.contactName;
      const user: ZIMUserFullInfo | undefined = userInfoList && userInfoList.userList ? userInfoList.userList.find((userInfo) => userInfo.baseInfo.userID === callUser.userID) : undefined;
      let userName = '';
      const avatar = contact?.imageUrl ?? (user ? user.baseInfo.userAvatarUrl : '');
      // có tên trong danh bạ thì lấy tên trong danh bạ
      // không có tên lấy tên trên hệ thống
      // không có tên hệ thống thì lấy số điện thoại
      // if (contactName) {
      //   userName = contactName;
      // } else {
      //   userName = user ? user.baseInfo.userName : replacePhoneNumber(callUser.userID);
      // }
      const nickName = extendedData.phoneCaller === getUserInfo()?.phoneNumber ? extendedData.calleeName : extendedData.callerName;
      userName = contactName ?? conversationNameCache[callUser.userID] ?? nickName ?? callUser.userID;

      listCallParticipant.push({
        userId: callUser.userID,
        userName,
        avatar,
        isMuted: true,
        isVideoOn: false,
        soundLevel: 0,
        state: callUser.state
      });
    });
    return listCallParticipant;
  }

  handleCallInvitationError(callID: string, endReason: CallEndReason) {
    this.callAudio.stop();
    this.broadcastService.call({ action: 'clear-ringtone', payload: {} });

    if (endReason === 'blocked') {
      // this.broadcastService.call({ action: 'show-blocked-notification', payload: { callId: callID } });
      const raw = localStorage.getItem('blocked_by');
      const parsed = raw ? JSON.parse(raw) : [];
      const list = Array.isArray(parsed) ? parsed : [];
      const currentInviter = this._currentCall()?.participants?.[0]?.userId || this._pendingInvitation()?.inviter;
      if (currentInviter && !list.includes(currentInviter)) {
        list.push(currentInviter);
        localStorage.setItem('blocked_by', JSON.stringify(list));
      }
    }
    if(this.localStream() && (endReason === 'normal' || endReason === 'ended')){
      this.callZegoService.stopPubishAndDestroyStream(this.localStream(), this.localStreamId());
    }

    // Nếu cuộc gọi trước đã kết thúc và cuộc gọi thứ 3 bị timeout hoặc cancelled thì đóng tab call
    if((endReason === 'timeout' || endReason === 'cancelled') && this._pendingInvitation()?.status === 'waiting' && !this.currentCall()){
      this._pendingInvitation.set(null);
      this.broadcastService.call({ action: 'clear-invitation', payload: { callId: callID } });
      this.broadcastService.call({ action: 'reset-call-state', payload: { callId: callID } });
      window.close();
      return;
    }

    //Nếu có cuộc gọi thứ 3 và cuộc gọi hiện tại end thì ko đóng tab call
    if (endReason === 'ended' || endReason === 'normal') {
      if(this._pendingInvitation()?.status === 'waiting'){
        this._callState.set('ended');
        const currentCall = this._currentCall();
        if (!currentCall) return;
        if (currentCall.callId !== callID)  return;
        this.callZegoService.logoutRoom(currentCall.callId);
        this._currentCall.set(null);
        return;
      }
    }

    this._pendingInvitation.set(null);
    this.broadcastService.call({ action: 'clear-invitation', payload: { callId: callID } })
    if (this._callState() === 'ended') return;
    const currentCall = this._currentCall();
    if (!currentCall) return;
    // nếu không phải cuộc gọi hiện tại thì không set error
    if (currentCall.callId !== callID)  return;

    this.broadcastService.call({ action: 'reset-call-state', payload: { callId: callID } });
    this.callZegoService.logoutRoom(currentCall.callId);

    // đã accept
    if (endReason === 'ended' || endReason === 'normal') {
      window.close();
      return;
    }

    // khi mình là người nhận
    if (currentCall.inviter !== this.zimService.userId) {
      if (!this.isInCallRoute()) {
        this.reset();
        return;
      }
    }

    // khi mình là người gọi -> thay đổi khi chưa accept
    this._callState.set('ended');
    this._currentCall.update(call => call ? { ...call, endTime: new Date(), endReason } : null);

  }

  isSwitchCall(extendedData: string): boolean {
    const switchCallExtendedData = safeJsonParse<SwitchCallExtendedData>(extendedData);
    if (switchCallExtendedData?.reason === 'switch-call' && switchCallExtendedData.userId === this.zimService.userId) {
      this.broadcastService.call({ action: 'clear-invitation', payload: { callId: switchCallExtendedData.callID } });
      return true;
    };
    return false;
  }

  setState(data: CallStoreState) {
    this._currentCall.set(data.currentCall);
    this._callState.set(data.callState);
    this._participants.set(data.participants);
    this._pendingInvitation.set(data.pendingInvitation);
    this.updateRingtone();
  }

  setPendingInvitation(pendingInvitation: CallInvitation | null) {
    this._pendingInvitation.set(pendingInvitation);
  }

  setCallState(callState: CallState) {
    this._callState.set(callState);
    this.updateRingtone();
  }

  setCallStartTime(callStartTime: number) {
    this._callStartTime.set(callStartTime);
  }

  setLocalStream(stream : ZegoLocalStream){
    this._localStream.set(stream);
  }

  setLocalStreamId(streamId: string){
    this._localStreamId.set(streamId);
  }
  //#region  Handle room user update
  // handleRoomUserUpdate(roomID: string, updateType: "DELETE" | "ADD", userList: ZegoUser[]){
  //   if(updateType === "ADD") {
  //     const now = Date.now();
  //     this._callStartTime.set(now);
  //     this.broadcastService.call({ action: 'sync-call-start-time', payload: { callStartTime: now } })
  //   }
  // }
  //#endregion

  //#region Handle remote media state
  handleRemoteMicStateChange(streamID: string, status: 'OPEN' | 'MUTE'): void{
    this._participants.set(this._participants().map((p) => {
      if(p.userId !== streamID){
        return p;
      } else{
        return {...p, isMuted: status === 'MUTE'}
      }
    }))
  }

  handleRemoteCamStateChange(streamID: string, status: 'OPEN' | 'MUTE'): void{
    if(this.currentCall()?.type === 'audio' && status === 'OPEN'){
      this.switchCallType();
    }
    this._participants.set(this._participants().map((p) => {
      if(p.userId !== streamID){
        return p;
      } else{
        return {...p, isVideoOn: status === 'OPEN'}
      }
    }))
  }

  handleSoundLevelUpdate(soundLevelList: ZegoSoundLevelInfo[]): void{
    soundLevelList.forEach(soundLevel => {
      if (soundLevel.streamID === this.localStreamId()) {
        this._localSoundLevel.set(soundLevel.soundLevel);
      } else {
        this._participants.set(this._participants().map((p) => {
          if(p.userId !== soundLevel.streamID){
            return p;
          } else{
            return {...p, soundLevel: soundLevel.soundLevel}
          }
        }))
      }
    })
  }

  async publishCallStream(callId: string, type: 'audio' | 'video') {
    const userInfo = getUserInfo()
    const isLoginRoom = await this.callZegoService.loginRoom(callId, this.zimService.userId ?? "", userInfo.username ?? "");
    this._isLoginRoom.set(isLoginRoom);

    if(this.isCamPermissionGranted() || this.isMicPermissionGranted()){
      if(type === "audio") {
        const streamObject = await this.callZegoService.createAndPublishStream(this.zimService.userId ?? "", this.isCamPermissionGranted(), this.isMicPermissionGranted(), 'audio');
        this.setLocalStream(streamObject?.localStream!);
        this.setLocalStreamId(streamObject?.streamID ?? "");
      } else {
        const streamObject = await this.callZegoService.createAndPublishStream(this.zimService.userId ?? "", this.isCamPermissionGranted(), this.isMicPermissionGranted(), 'video');
        this.setLocalStream(streamObject?.localStream!);
        this.setLocalStreamId(streamObject?.streamID ?? "");
      }
    }

    this.updateLocalMediaState('audio', this.localStream() ? this.localStream()!.getAudioTracks().length > 0 && this.localStream()!.getAudioTracks()[0].enabled : false);
    this.updateLocalMediaState('video', this.localStream() ? this.localStream()!.getVideoTracks().length > 0 && this.localStream()!.getVideoTracks()[0].enabled : false);

    // đồng bộ trạng thái ban đầu mic cho call status bar
    this.broadcastService.call({ action: 'sync-mic-state', payload: { micState: this.localStream() ? this.localStream()!.getAudioTracks().length > 0 && this.localStream()!.getAudioTracks()[0].enabled : false } })
  }

  updateLocalMediaState(type: 'audio' | 'video', state: boolean) {
    if(type === 'audio') {
      this._localMicState.set(state);
    } else {
      this._localCamState.set(state);
    }
  }

  handleNetworkQuality(userID: string, upstreamQuality: number, downstreamQuality: number) {
    // Bỏ qua nếu chưa có dữ liệu
    if (upstreamQuality === ZegoQualityGrade.Unknown
      && downstreamQuality === ZegoQualityGrade.Unknown
      && !this._isLoginRoom()) return;

    if (!userID) {
      // MẠNG CỦA MÌNH (Local network)
      if (!this.localDieByDisconnect) {
        this.handleNetworkStateChange('local', { upstreamQuality, downstreamQuality } as NetworkQualityData);
      }
    } else {
      // MẠNG CỦA ĐỐI PHƯƠNG (Remote network)
      this.handleNetworkStateChange('remote', { userId: userID, upstreamQuality, downstreamQuality } as RemoteNetworkQualityData);
    }
  }

  private isWeakNetwork(qualityData: NetworkQualityData) {
    const isWeakUpstream =
        qualityData.upstreamQuality == ZegoQualityGrade.Poor || qualityData.upstreamQuality == ZegoQualityGrade.Die

    const isWeakDownstream =
        qualityData.downstreamQuality == ZegoQualityGrade.Poor || qualityData.downstreamQuality == ZegoQualityGrade.Die

    // Chỉ cần 1 chiều yếu là đủ để coi là lag
    return isWeakUpstream || isWeakDownstream
  }

  private isPoor(qualityData: NetworkQualityData) {
    return qualityData.upstreamQuality === ZegoQualityGrade.Poor && qualityData.downstreamQuality === ZegoQualityGrade.Poor;
  }

  private isDie(qualityData: NetworkQualityData) {
    return qualityData.upstreamQuality === ZegoQualityGrade.Die && qualityData.downstreamQuality === ZegoQualityGrade.Die;
  }

  private handleNetworkStateChange(type: 'local' | 'remote', qualityData: NetworkQualityData | RemoteNetworkQualityData) {
    this._networkQualityInfo.update(info => {
      const updated = { ...info };

      if (type === 'local') {
        // Lưu trạng thái cũ để so sánh
        const oldLocalState = { ...updated.localState };
        updated.localState = qualityData as NetworkQualityData;

        // Kiểm tra trạng thái mạng local
        if (this.isDie(qualityData)) {
          console.log('Local network died');

          // Chỉ start timer nếu chuyển từ trạng thái khác sang Die
          // VÀ remote chưa có ai Die
          const wasNotDie = !this.isDie(oldLocalState);
          const hasAnyRemoteDie = updated.remoteState.some(remote => this.isDie(remote));

          if (wasNotDie && !hasAnyRemoteDie) {
            this.startNetworkQualityTimer();
          }

        } else if (this.isPoor(qualityData)) {
          console.log('Local network is poor');
          // Không cần start timer cho Poor của local

        } else if (!this.isWeakNetwork(qualityData)) {
          console.log('Local network recovered');

          // Clear timer nếu local tốt và tất cả remote đều tốt
          const allRemoteGood = updated.remoteState.every(remote => !this.isWeakNetwork(remote));
          if (allRemoteGood) {
            this.clearNetworkQualityTimer();
          }
        }

      } else {
        // Cập nhật remote state
        const remoteData = qualityData as RemoteNetworkQualityData;
        const existingIndex = updated.remoteState.findIndex(r => r.userId === remoteData.userId);

        // Lưu trạng thái cũ để so sánh
        const oldRemoteState = existingIndex >= 0 ? { ...updated.remoteState[existingIndex] } : null;

        if (existingIndex >= 0) {
          updated.remoteState[existingIndex] = remoteData;
        } else {
          updated.remoteState.push(remoteData);
        }

        // Kiểm tra trạng thái mạng của remote user
        if (this.isDie(remoteData)) {
          console.log(`Remote network died for user ${remoteData.userId}`);

          // Start timer nếu:
          // 1. Remote chuyển từ trạng thái khác sang Die (không phải Die -> Die)
          // 2. VÀ local chưa Die
          const wasNotDie = !oldRemoteState || !this.isDie(oldRemoteState);
          const isLocalDie = this.isDie(updated.localState);

          if (wasNotDie && !isLocalDie) {
            this.startNetworkQualityTimer();
          }

        } else if (this.isPoor(remoteData)) {
          console.log(`Remote network is poor for user ${remoteData.userId}`);
          // Remote Poor không cần start timer, chỉ hiển thị warning

        } else if (!this.isWeakNetwork(remoteData)) {
          console.log(`Remote network recovered for user ${remoteData.userId}`);

          // Clear timer nếu local tốt và tất cả remote đều tốt
          const allRemoteGood = updated.remoteState.every(remote => !this.isWeakNetwork(remote));
          if (!this.isWeakNetwork(updated.localState) && allRemoteGood) {
            this.clearNetworkQualityTimer();
          }
        }
      }

      return updated;
    });
  }

  private startNetworkQualityTimer() {
    // Clear existing timer
    this.clearNetworkQualityTimer();

    // Start new 30-second countdown
    this.networkQualityTimer = setTimeout(() => {
      console.log(`Network timeout after 30 seconds - ending call`);
      this.handleNetworkTimeout();
    }, this.NETWORK_TIMEOUT);
  }

  private clearNetworkQualityTimer() {
    if (this.networkQualityTimer) {
      clearTimeout(this.networkQualityTimer);
      this.networkQualityTimer = null;
    }
  }

  private handleNetworkTimeout() {
    // Kết thúc cuộc gọi sau 30 giây mạng kém
    const call = this._currentCall();
    if (!call) return;

    console.log('Network timeout - forcing call end');

    // Kết thúc cuộc gọi

    this.handleCallInvitationError(call.callId, 'network');
    this.broadcastService.call({ action: 'reset-call-state', payload: { callId: call.callId } });

    try {
      if (call.inviter === this.zimService.userId) {
        this.callZimService.callEnd(call.callId, { extendedData: '' });
      } else {
        this.callZimService.callQuit(call.callId, { extendedData: '' });
      }
    } catch (error) {
      console.log("handleNetworkTimeout", error);
    }
  }

  private forceLocalNetworkDie(reason: string) {
    console.log('Force local Die:', reason);

    this.localDieByDisconnect = true;

    this.handleNetworkStateChange('local', {
      upstreamQuality: ZegoQualityGrade.Die,
      downstreamQuality: ZegoQualityGrade.Die
    });
  }

  private onLocalReconnected() {
    if (!this.localDieByDisconnect) return;

    console.log('Local reconnected - waiting for real quality');

    // Chỉ clear timer cưỡng bức nếu bạn muốn
    this.clearNetworkQualityTimer();

    this.localDieByDisconnect = false;
  }

  switchCallType(){
    this._currentCall.update(call => call ? { ...call, type: 'video' } : null);
  }

  forceEndCall(payload: { currentCall: CurrentCall | null, callState: CallState }) {
    this.callAudio.stop();
    this.broadcastService.call({ action: 'clear-ringtone', payload: {} });
    if (!payload.currentCall) return;
    switch (payload.callState) {
      case 'outgoing':
      case 'ringing':
        const invitees: string[] = payload.currentCall.participants.map(p => p.userId);
        this.cancelCall(invitees);
        break;
      case 'connected':
        if (payload.currentCall.inviter === this.zimService.userId) {
          this.endCall();
        } else {
          this.callQuit(payload.currentCall.callId);
        }
        break;
    }
    this.reset();
  }

  private updateRingtone(): void {
    const state = this._callState();
    const pending = this._pendingInvitation();
    const currentCall = this._currentCall();

    // luôn stop trước
    this.callAudio.stop();

    // đã connect thì không bao giờ đổ chuông
    if (state === 'connected') return;

    if (this.isInCallRoute()) return;

    const isCaller = !!currentCall && currentCall.inviter === this.zimService.userId;

    const isCallee = !isCaller && !!pending;

    // Không có call hợp lệ
    if (!isCaller && !isCallee) return;

    if (this.hasCalleeIncomingCall()) {
      this.callAudio.play('callee-incoming');
      return;
    }

    if (state === 'ringing') {
      this.callAudio.play('ringing');
      return;
    }

    if (state === 'outgoing') {
      this.callAudio.play('outgoing');
      return;
    }
  }

  // Wait for call service to be ready with retry mechanism
  private async waitForServiceReady(): Promise<void> {
    await this.retryService.retryUntilCondition(
      () => this.callZimService.isReady(),
      {
        maxAttempts: 3,
        delayMs: 1000,
        backoffMultiplier: 2,
        onRetry: (attempt, maxAttempts) => {
          console.log(`Đang chờ call service sẵn sàng... (lần thử ${attempt}/${maxAttempts})`);
        }
      },
      'Call service không khả dụng sau nhiều lần thử. Vui lòng kiểm tra kết nối.'
    );
  }

  private createNewInvitation(
     createTo: "Received" | "Created",
     callId: string,
     participants: CallParticipant[],
     extendedData: PayloadDict,
     invitation?: CallInvitation) {

    const newCall: CurrentCall = {
      callId,
      type: extendedData.isVideoCall ? 'video' : 'audio',
      state: 'outgoing',
      participants: participants,
      startTime: new Date(),
      inviter: invitation ? invitation.inviter : this.zimService.userId,
      extendedData
    };

    this._currentCall.set(newCall);
    this._callState.set('outgoing');
    this._participants.set(newCall.participants);

    if (createTo === "Received" && invitation) {
      let isInMeeting = !!localStorage.getItem("ACTIVE_MEETING");

      if(isInMeeting){
        invitation.status = 'waiting';
      }
      this._pendingInvitation.set(invitation);

      this.updateRingtone();
    } else {
      this._pendingInvitation.set(null);
    }
  }

  private isExpired(startTime: Date, seconds = 60): boolean {
    return Date.now() - startTime.getTime() > seconds * 1000;
  }

  // Cleanup
  reset(): void {
    this._currentCall.set(null);
    this._callState.set('idle');
    this._participants.set([]);
    this._pendingInvitation.set(null);

    // Clear network quality state
    this.clearNetworkQualityTimer();
    this._networkQualityInfo.set({
      localState: { upstreamQuality: ZegoQualityGrade.Unknown, downstreamQuality: ZegoQualityGrade.Unknown },
      remoteState: [],
    });
  }

  // Unlisten events when service is destroyed (if needed)
  destroy(): void {
    // Unlisten all events to prevent memory leaks
    this.callZimService.unlistenBlacklistChanged();
    this.callZimService.unlistenCallInvitationReceived();
    this.callZimService.unlistenCallInvitationCreated();
    this.callZimService.unlistenCallInvitationCancelled();
    this.callZimService.unlistenCallInvitationTimeout();
    this.callZimService.unlistenCallInvitationEnded();
    this.callZimService.unlistenCallUserStateChanged();
  }
}
